import { type GridApi } from "ag-grid-community"
import { type DependencyList, useEffect } from "react"
import { useEventCallback } from "usehooks-ts"

import { getGridApiById, subscribeGridApiChange } from "./useGridState"

/**
 * `useEffect` over the table's registered grid: runs `effect` with the grid api now and whenever
 * the table registers a grid, cleaning up the previous run first. Nothing runs while no grid is
 * registered. Unlike `useIsGridRegistered`, registration doesn't re-render the component.
 */
export function useRegisteredGridEffect(
  tableId: string,
  effect: (api: GridApi) => void | (() => void),
  deps: DependencyList,
) {
  const latestEffect = useEventCallback(effect)
  useEffect(() => {
    let cleanup: void | (() => void)
    const run = () => {
      cleanup?.()
      cleanup = undefined
      const api = getGridApiById(tableId)
      if (api && !api.isDestroyed()) cleanup = latestEffect(api)
    }
    run()
    const unsubscribe = subscribeGridApiChange(tableId, run)
    return () => {
      unsubscribe()
      cleanup?.()
    }
    // `deps` are the effect's, as with useEffect; `latestEffect` is stable.
  }, [tableId, latestEffect, ...deps])
}
